import React, { ReactNode } from 'react';
import AlertType from '@app/model/alert/AlertType';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import AlertInput from '@app/model/alert/input/AlertInput';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import AlertScheduleType from '@app/model/alert/AlertScheduleType';
import { useQuery } from '@tanstack/react-query';
import FormPlaceholder from '@app/component/placeholder/FormPlaceholder';
import AlertService from '@app/model/alert/service/AlertService';
import { AlertQueryKeys } from '@app/model/alert/service/AlertQueryKeys';
import Message from '@app/component/message/Message';
import _isEmpty from 'lodash/isEmpty';
import PriceComparisonAlertRulePricePoint from '@app/model/alert/PriceComparisonAlertRulePricePoint';
import PriceComparisonAlertRuleTrigger from '@app/model/alert/PriceComparisonAlertRuleTrigger';
import CompetitorsPriceRateAlertRuleInclusion from '@app/model/alert/CompetitorsPriceRateAlertRuleInclusion';


const AlertFormInitializationFragment: React.FunctionComponent<{
	alertId?: number | undefined,
	alertType: AlertType,
	companyHotel: CompanyHotel,
	isCopy: boolean,
	renderForm: (companyHotel: CompanyHotel, defaultValues: AlertInput) => ReactNode
}> = ({ alertId, alertType, companyHotel, isCopy, renderForm }) => {

	return !!alertId ?
		<EditAlertForm alertId={alertId} alertType={alertType} companyHotel={companyHotel} isCopy={isCopy}
									 renderForm={renderForm} /> :
		<NewAlertForm alertType={alertType} companyHotel={companyHotel} isCopy={isCopy} renderForm={renderForm} />;
};

const EditAlertForm: React.FunctionComponent<{
	alertId: number,
	alertType: AlertType,
	companyHotel: CompanyHotel,
	isCopy: boolean,
	renderForm: (companyHotel: CompanyHotel, defaultValues: AlertInput) => ReactNode
}> = ({ alertId, alertType, companyHotel, isCopy, renderForm }) => {

	const { data: alertOutput, isSuccess, isLoading } = useQuery({
		queryFn: () => AlertService.findAlertById(alertId),
		queryKey: AlertQueryKeys.detail(alertId),
	});

	if (isLoading) return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8"><FormPlaceholder /></div>;

	if (isSuccess) {
		const defaultValues: AlertInput = {

			id: isCopy ? undefined : alertOutput.alert.id,
			hotelId: alertOutput.alert.companyHotelId.hotelId,
			name: alertOutput.alert.name + (isCopy ? ' Copy' : ''),
			type: alertOutput.alert.type,
			filter: {
				//@ts-ignore
				roomTypes: alertOutput.alert.filter.roomTypes,
				//@ts-ignore
				onlyRefundable: !alertOutput.alert.filter.onlyRefundable ? 'false' : 'true',
				lengthOfStay: '' + alertOutput.alert.filter.lengthOfStay,
			},
			schedule: {
				time: alertOutput.alert.schedule.time,
				type: alertOutput.alert.schedule.type,
				// @ts-ignore
				weekdays: _isEmpty(alertOutput.alert.schedule.weekdays) ? ['ALL'] : alertOutput.alert.schedule.weekdays,

			},
			timeframe: alertOutput.alert.timeframe,
			competitorsPriceRateAlertRuleInput: alertOutput.competitorsPriceRateAlertRule,
			priceComparisonAlertRuleInput: alertOutput.priceComparisonAlertRule,
			extraEmails: alertOutput.alert.extraEmails,
		};

		return renderForm(companyHotel, defaultValues);
	}

	return <div className="mx-auto max-w-7xl py-6 px-4 sm:px-6 lg:px-8">
		<Message state="warning">Alert could not be found!</Message>
	</div>;
};

const NewAlertForm: React.FunctionComponent<{
	alertType: AlertType,
	companyHotel: CompanyHotel,
	isCopy: boolean,
	renderForm: (companyHotel: CompanyHotel, defaultValues: AlertInput) => ReactNode
}> = ({ alertType, companyHotel, isCopy, renderForm }) => {
	const defaultValues: AlertInput = {
		hotelId: companyHotel!.hotel.id,
		name: '',
		type: alertType,
		filter: {
			roomTypes: companyHotel.roomMappingTypeConfiguration!.configs.filter(rt => rt.isEnabled && rt.type !== HotelRoomMappingType.NONE && rt.type !== HotelRoomMappingType.SINGLE)
				.map(rt => rt.type),
			// @ts-ignore
			onlyRefundable: 'false',
			// @ts-ignore
			lengthOfStay: '1',
		},
		// @ts-ignore
		schedule: {
			type: AlertScheduleType.TRIGGERED,
		},
		timeframe: {
			days: 7,
		},
		// @ts-ignore
		priceComparisonAlertRuleInput: {
			pricePoint: PriceComparisonAlertRulePricePoint.MEDIAN,
			trigger: PriceComparisonAlertRuleTrigger.INSIDE_PRICE_RANGE,
		},
		// @ts-ignore
		competitorsPriceRateAlertRuleInput: {
			competitorsInclusion: CompetitorsPriceRateAlertRuleInclusion.ALL,
		},
		extraEmails: [],
	};
	return renderForm(companyHotel, defaultValues);
};

export default AlertFormInitializationFragment;